import { useState } from "react";
import type { Patient } from "../../../types";
import { formatDisplayDate } from "../../../utils/displayDate";
import "./DiagnosisSection.scss";

interface DiagnosisSectionProps {
  patient: Patient;
  showDiseaseStatus?: boolean;
}

/**
 * Render the patient's diagnosis, stage, treatment line, and clinical details.
 *
 * @param props - Patient and whether the radiology disease status is displayed.
 * @returns The diagnosis summary section.
 */
export function DiagnosisSection({
  patient,
  showDiseaseStatus = false,
}: DiagnosisSectionProps) {
  const [isEcogHovered, setIsEcogHovered] = useState(false);
  const cancers = patient.cancers || [];

  return (
    <div className="diagnosis-section">
      {cancers.length > 0 ? (
        <>
          {/* Multiple Primary Malignancy — one card per MD-note cancer (scrolls right when >3). */}
          <div className="diagnosis-header">
            <div className="diagnosis-title-group">
              <h2 className="diagnosis-title">
                {cancers.length > 1
                  ? "Multiple Primary Malignancy"
                  : patient.diagnosis || "Diagnosis"}
                {cancers.length > 1 && (
                  <span className="mpm-count-badge">
                    {cancers.length} Cancer{" "}
                    {cancers.length === 1 ? "diagnosis" : "diagnoses"} found
                  </span>
                )}
              </h2>
              {cancers.length === 1 &&
                (cancers[0].histology || cancers[0].diagnosedOn) && (
                  <p className="diagnosis-subtitle">
                    {cancers[0].histology}
                    {cancers[0].histology &&
                      cancers[0].diagnosedOn &&
                      " · "}
                    {cancers[0].diagnosedOn &&
                      `Diagnosed on ${formatDisplayDate(cancers[0].diagnosedOn)}`}
                  </p>
                )}
            </div>
            {patient.isCancer && (patient.stage || patient.lineOfTherapy) && (
              <div className="diagnosis-badges">
                {patient.stage && (
                  <div className="diagnosis-badge stage-badge">
                    <span className="badge-label">Stage</span>
                    <span className="badge-value">{patient.stage}</span>
                  </div>
                )}
                {patient.lineOfTherapy && (
                  <div className="diagnosis-badge line-badge">
                    <span className="badge-label">Line</span>
                    <span className="badge-value">{patient.lineOfTherapy}</span>
                  </div>
                )}
              </div>
            )}
          </div>
          {cancers.length > 1 && (
            <div className="mpm-cards">
              {cancers.map((cancer, index) => (
                <div key={`${cancer.code}-${index}`} className="mpm-card">
                  <div className="mpm-card-head">
                    <span className="mpm-card-name">
                      {cancer.description || cancer.code}
                    </span>
                    {cancer.diagnosedOn && (
                      <span className="mpm-card-date">
                        Diagnosed on: {formatDisplayDate(cancer.diagnosedOn)}
                      </span>
                    )}
                  </div>
                  {(cancer.stage || cancer.histology) && (
                    <div className="mpm-card-body">
                      {cancer.stage && (
                        <div className="mpm-card-stage">{cancer.stage}</div>
                      )}
                      {cancer.histology && (
                        <div className="mpm-card-histology">
                          {cancer.histology}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="diagnosis-header">
          <div className="diagnosis-title-group">
            <h2 className="diagnosis-title">
              {patient.diagnosis || "Diagnosis unavailable"}
            </h2>
            <p className="diagnosis-subtitle">
              {(patient.mdCancerCodes?.length
                ? patient.mdCancerCodes
                : patient.dxCode
                  ? [patient.dxCode]
                  : []
              ).map((code) => (
                <span key={code} className="diagnosis-code-chip">
                  {code}
                </span>
              ))}
              {patient.isCancer && patient.histology && (
                <span>{patient.histology} · </span>
              )}
              {patient.isCancer && patient.diagnosisDate && (
                <span>
                  Diagnosed On {formatDisplayDate(patient.diagnosisDate)}
                </span>
              )}
            </p>
          </div>
          {patient.isCancer && (
            <div className="diagnosis-badges">
              {patient.stage && (
                <div className="diagnosis-badge stage-badge">
                  <span className="badge-label">Stage</span>
                  <span className="badge-value">{patient.stage}</span>
                </div>
              )}
              {patient.lineOfTherapy && (
                <div className="diagnosis-badge line-badge">
                  <span className="badge-label">Line</span>
                  <span className="badge-value">{patient.lineOfTherapy}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Rich cancer band (TNM / metastatic sites / ECOG / disease status) — shown
          only for cancer MRNs. Non-cancer patients show just the code + description. */}
      {patient.isCancer && (
        <div className="diagnosis-details-grid">
          {/* TNM Classification */}
          {patient.tnmClassification && (
            <div className="diagnosis-detail-card">
              <div className="detail-card-label">TNM Classification</div>
              <div className="detail-card-value">
                {patient.tnmClassification}
              </div>
            </div>
          )}

          {/* Metastatic Sites */}
          <div className="diagnosis-detail-card">
            <div className="detail-card-label">Metastatic Sites</div>
            {patient.metastaticSites && patient.metastaticSites.length > 0 ? (
              <div className="metastatic-sites">
                {patient.metastaticSites.map((site, index) => (
                  <span key={index} className="metastatic-site-pill">
                    {site}
                  </span>
                ))}
              </div>
            ) : (
              <div className="metastatic-sites">
                <span className="metastatic-site-pill no-metastasis-pill">
                  No Metastasis
                </span>
              </div>
            )}
          </div>

          {/* ECOG Status */}
          {patient.ecogStatus !== undefined && (
            <div
              className="diagnosis-detail-card ecog-card"
              onMouseEnter={() => setIsEcogHovered(true)}
              onMouseLeave={() => setIsEcogHovered(false)}
              style={{
                position: "relative",
                cursor: patient.ecogStatusDate ? "pointer" : "default",
              }}
            >
              <div className="detail-card-label">ECOG Status</div>
              <div className="detail-card-value">{patient.ecogStatus}</div>
              {isEcogHovered && patient.ecogStatusDate && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    marginTop: "0.5rem",
                    backgroundColor: "white",
                    border: "1px solid var(--color-border)",
                    borderRadius: "0.375rem",
                    padding: "0.5rem 0.75rem",
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    zIndex: 1000,
                    whiteSpace: "nowrap",
                    fontSize: "var(--font-size-xs)",
                    color: "var(--color-text-primary)",
                  }}
                >
                  ECOG is till {formatDisplayDate(patient.ecogStatusDate)}
                </div>
              )}
            </div>
          )}

          {/* Disease Status */}
          {showDiseaseStatus && patient.diseaseStatus && (
            <div className="diagnosis-detail-card">
              <div className="detail-card-label">Disease Status</div>
              <div className="detail-card-value">{patient.diseaseStatus}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
